<template>
  <section class="priority-support" :aria-labelledby="titleId">
    <header class="priority-support__header">
      <h1 :id="titleId">{{ t('enterprise_priority_support_title') }}</h1>
      <p>{{ t('enterprise_priority_support_description') }}</p>
    </header>

    <div v-if="isVipSite" class="priority-support__channels priority-support__channels--domestic">
      <article class="support-card">
        <SupportCardHeader
          icon="message-circle"
          tone="wechat"
          :title="t('enterprise_priority_support_wechat')"
          :description="t('enterprise_priority_support_wechat_description')"
        />
        <QrPreview :src="config.domestic.wechatGroupQrUrl" />
        <SupportValue
          :value="t('enterprise_priority_support_wechat_backup', { ID: config.domestic.wechatBackupId || '—' })"
          :action="t('enterprise_priority_support_copy')"
          :disabled="!config.domestic.wechatBackupId"
          @action="copy(config.domestic.wechatBackupId)"
        />
      </article>

      <article class="support-card">
        <SupportCardHeader
          icon="messages-square"
          tone="qq"
          :title="t('enterprise_priority_support_qq')"
          :description="t('enterprise_priority_support_qq_description')"
        />
        <QrPreview :src="config.domestic.qqGroupQrUrl" />
        <SupportValue
          :value="t('enterprise_priority_support_qq_number', { N: config.domestic.qqGroupNumber || '—' })"
          :action="t('enterprise_priority_support_copy')"
          :disabled="!config.domestic.qqGroupNumber"
          @action="copy(config.domestic.qqGroupNumber)"
        />
      </article>

      <article class="support-card">
        <SupportCardHeader
          icon="mail"
          tone="email"
          :title="t('enterprise_priority_support_email')"
          :description="t('enterprise_priority_support_email_description')"
        />
        <div class="support-card__spacer" />
        <SupportValue
          :value="config.domestic.email || '—'"
          :action="t('enterprise_priority_support_copy')"
          :disabled="!config.domestic.email"
          ltr
          @action="copy(config.domestic.email)"
        />
      </article>
    </div>

    <div v-else class="priority-support__channels priority-support__channels--international">
      <article class="support-card">
        <SupportCardHeader
          icon="twitter"
          tone="x"
          :title="t('enterprise_priority_support_x')"
          :description="t('enterprise_priority_support_x_description')"
        />
        <div class="support-card__spacer" />
        <SupportValue
          :value="config.international.xAccount || '—'"
          :action="t('enterprise_priority_support_open')"
          :disabled="!config.international.xUrl"
          ltr
          @action="openExternal(config.international.xUrl)"
        />
      </article>

      <article class="support-card">
        <SupportCardHeader
          icon="mail"
          tone="email"
          :title="t('enterprise_priority_support_email')"
          :description="t('enterprise_priority_support_email_description')"
        />
        <div class="support-card__spacer" />
        <SupportValue
          :value="config.international.email || '—'"
          :action="t('enterprise_priority_support_copy')"
          :disabled="!config.international.email"
          ltr
          @action="copy(config.international.email)"
        />
      </article>
    </div>

    <div class="priority-support__tip">
      <WorkbenchIcon name="info" :size="13" />
      <span>{{ t('enterprise_priority_support_footer') }}</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import Toast from '~/utils/toast'
import AccountServer from '~/services/AccountServer'
import type { PrioritySupportConfig } from '~/services/AccountServer/types'
import { NetCode } from '~/types/const'
import { useSiteConfig } from '~/config/useSiteConfig'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import SupportCardHeader from './SupportCardHeader.vue'
import SupportValue from './SupportValue.vue'
import QrPreview from './QrPreview.vue'

const { t } = useI18n()
const { isVipSite } = useSiteConfig()
const titleId = useId()

const emptyConfig = (): PrioritySupportConfig => ({
  domestic: {
    wechatGroupQrUrl: '',
    wechatBackupId: '',
    qqGroupQrUrl: '',
    qqGroupNumber: '',
    email: '',
  },
  international: {
    xAccount: '',
    xUrl: '',
    email: '',
  },
})

const config = ref<PrioritySupportConfig>(emptyConfig())

async function loadConfig() {
  try {
    const response = await AccountServer.getPrioritySupportConfig()
    if (response.code === NetCode.SUCCESS && response.data) {
      config.value = {
        domestic: { ...emptyConfig().domestic, ...response.data.domestic },
        international: { ...emptyConfig().international, ...response.data.international },
      }
    }
  } catch (error) {
    console.error('priority support config load failed', error)
  }
}

async function copy(value: string) {
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    Toast(t('chat_toast_copy_success'), 'success')
  } catch (error) {
    console.error('priority support copy failed', error)
  }
}

function openExternal(url: string) {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

onMounted(loadConfig)
</script>

<style scoped lang="scss">
.priority-support {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  color: #1f2329;
}

.priority-support__header {
  display: flex;
  flex-direction: column;
  gap: 6px;

  h1,
  p {
    margin: 0;
  }

  h1 {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.4;
  }

  p {
    color: #646a73;
    font-size: 12.5px;
    line-height: 1.6;
  }
}

.priority-support__channels {
  display: flex;
  gap: 16px;
  width: 100%;

  &--domestic {
    height: 296px;

    .support-card {
      flex: 1 1 0;
    }
  }

  &--international {
    height: 168px;

    .support-card {
      flex: 0 1 389px;
      width: 389px;
    }
  }
}

.support-card {
  box-sizing: border-box;
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border: 1px solid #e8ebf0;
  border-radius: 12px;
  background: #fff;
}

.support-card__spacer {
  flex: 1;
}

.priority-support__tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f7f9fc;
  color: #646a73;
  font-size: 12px;
  line-height: 1.6;

  .workbench-icon {
    color: #98a2b3;
  }
}

@media (max-width: 1080px) {
  .priority-support__channels {
    height: auto;
    flex-wrap: wrap;

    .support-card {
      min-height: 168px;
      flex: 1 1 320px;
      width: auto;
    }
  }
}
</style>
